import { Box, Group, Text, Textarea } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import React from 'react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { useAsyncAction } from 'src/design/molecules/useAsyncAction';
import { Modal } from 'src/design/molecules/Modal';
import { ImageThumbnail } from 'src/design/organisms/ImageUpload/ImageThumbnail';
import { useResolvedImageUrl } from 'src/design/organisms/ImageUpload/useResolvedImageUrl';

export interface IImageReviewCardProps {
  id: string;
  comment?: string;
  size?: number;
  /** Read-only: shows the comment as text and hides the remove control. */
  disabled?: boolean;
  resolveImageUrl: (id: string) => Promise<string>;
  onDownloadClick: () => void;
  onRemoveClick?: () => void;
  onCommentSave: (id: string, comment: string) => Promise<void>;
}

/** @registry Image tile with a comment textarea and full-size preview modal; composes ImageThumbnail for the tile itself. */
export const ImageReviewCard: React.FC<IImageReviewCardProps> = ({
  id,
  comment = '',
  size = 300,
  disabled = false,
  resolveImageUrl,
  onDownloadClick,
  onRemoveClick,
  onCommentSave,
}) => {
  const [tmpComment, setTmpComment] = React.useState(comment);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // Resolved lazily on open (enabled=previewOpen) rather than sharing
  // ImageThumbnail's tile-sized resolution, so the preview stays independent
  // of the tile's render state.
  const { src: previewSrc } = useResolvedImageUrl(
    id,
    resolveImageUrl,
    previewOpen,
  );

  const save = useAsyncAction(onCommentSave);

  const description = comment || 'Uploaded image';

  return (
    <>
      <Box mb="md">
        <ImageThumbnail
          id={id}
          alt={description}
          size={size}
          resolveImageUrl={resolveImageUrl}
          onClick={() => setPreviewOpen(true)}
        />
        <Group gap="xs" mt="xs">
          {!disabled && onRemoveClick && (
            <DeleteActionIcon
              variant="light"
              label="Remove image"
              onDelete={onRemoveClick}
            />
          )}
          <BasicActionIcon
            variant="light"
            label="Download image"
            onClick={onDownloadClick}
          >
            <IconDownload size={20} />
          </BasicActionIcon>
        </Group>
        {disabled ? (
          <Text mt="sm">{comment}</Text>
        ) : (
          <>
            <Textarea
              label="Comment"
              placeholder="Add comment..."
              mt="md"
              value={tmpComment}
              onChange={(e) => setTmpComment(e.currentTarget.value)}
              maxRows={4}
              autosize
            />
            <Group justify="flex-end" mt="xs">
              <BasicButton
                size="xs"
                onClick={() => save.run(id, tmpComment)}
                disabled={tmpComment === comment}
                loading={save.loading}
              >
                Save comment
              </BasicButton>
            </Group>
          </>
        )}
      </Box>
      <Modal
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Image Preview"
        size="lg"
      >
        {previewSrc && (
          <img
            src={previewSrc}
            width={600}
            alt={description}
            style={{ display: 'block', objectFit: 'cover' }}
          />
        )}
        <Text mt="sm">{comment}</Text>
      </Modal>
    </>
  );
};
